import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import ProductCard from "@/pages/home/components/ProductCard";
import {
  useFeaturedProducts,
  useCategories,
  useVariantsByProductIds,
} from "@/hooks/useProducts";

interface CollectionTheme {
  slug: string;
  title: string;
  filters: string[];
  categoryHint?: string;
}

const COLLECTIONS: Record<string, CollectionTheme> = {
  gifts: {
    slug: "gifts",
    title: "Presentes",
    filters: ["Todos", "Até R$ 100", "R$ 100 — R$ 300", "Acima de R$ 300"],
  },
  "staff-picks": {
    slug: "staff-picks",
    title: "Escolha do Time",
    filters: ["Todos"],
  },
  "home-favorites": {
    slug: "home-favorites",
    title: "Favoritos da Casa",
    filters: ["Todos", "Iluminação", "Cerâmica", "Têxteis", "Organização"],
    categoryHint: "home",
  },
  vintage: {
    slug: "vintage",
    title: "Retrô",
    filters: ["Todos", "Anos 50", "Anos 60", "Anos 70", "Anos 80"],
    categoryHint: "vintage",
  },
};

const HERO_BY_SLUG: Record<string, string> = {
  gifts:
    "https://readdy.ai/api/search-image?query=Refined%20editorial%20overhead%20still%20life%20of%20a%20single%20handmade%20gift%20wrapped%20in%20natural%20kraft%20paper%20tied%20with%20a%20linen%20ribbon%2C%20a%20sprig%20of%20dried%20eucalyptus%20and%20a%20small%20handwritten%20paper%20tag%20resting%20on%20a%20warm%20cream%20linen%20backdrop%2C%20soft%20diffused%20daylight%20from%20the%20left%2C%20long%20gentle%20shadows%2C%20muted%20warm%20palette%2C%20minimalist%20mood%2C%20magazine%20product%20photography&width=900&height=1125&seq=casaflor-col-gifts&orientation=portrait",
  "staff-picks":
    "https://readdy.ai/api/search-image?query=Warm%20minimalist%20editorial%20photo%20of%20curated%20handmade%20ceramic%20vase%2C%20linen%20apron%2C%20leather%20notebook%20arranged%20on%20a%20wooden%20studio%20bench%2C%20daylight%20from%20a%20tall%20window%2C%20soft%20shadows%2C%20magazine%20style&width=1600&height=720&seq=casaflor-col-staff&orientation=landscape",
  "home-favorites":
    "https://readdy.ai/api/search-image?query=Cinematic%20wide%20editorial%20interior%20of%20a%20sun-drenched%20cozy%20living%20room%20corner%20with%20a%20warm%20linen%20couch%2C%20low%20handmade%20ceramic%20table%20lamp%2C%20woven%20jute%20rug%2C%20dried%20pampas%20in%20a%20stoneware%20vase%2C%20soft%20afternoon%20window%20light%20casting%20long%20shadows%20on%20a%20cream%20wall%2C%20minimalist%20mood%2C%20magazine%20photography%2C%20warm%20natural%20palette&width=1800&height=1000&seq=casaflor-col-home&orientation=landscape",
  vintage:
    "https://readdy.ai/api/search-image?query=Vintage%20editorial%20photograph%20of%20antique%20brass%20candlesticks%2C%20leather-bound%20books%2C%20sepia-toned%20ceramic%20vase%2C%20dried%20flowers%20on%20a%20warm%20wooden%20surface%2C%20soft%20daylight%20from%20window%2C%20nostalgic%20mood%2C%20documentary%20style&width=1600&height=720&seq=casaflor-col-vintage&orientation=landscape",
};

export default function CollectionPage() {
  const { slug } = useParams<{ slug: string }>();
  const theme = (slug && COLLECTIONS[slug]) || COLLECTIONS.gifts;

  const { categories } = useCategories();
  const matchedCategoryId = useMemo<number | null>(() => {
    if (!theme.categoryHint) return null;
    const hint = theme.categoryHint.toLowerCase();
    const found = categories.find((c) => c.name.toLowerCase().includes(hint));
    return found?.id ?? null;
  }, [categories, theme.categoryHint]);

  const { products, loading, error, refetch } = useFeaturedProducts({
    limit: 16,
    categoryId: matchedCategoryId,
  });

  const productIds = useMemo(() => products.map((p) => p.id), [products]);
  const { variantsByProduct } = useVariantsByProductIds(productIds);

  const [activeFilter, setActiveFilter] = useState<string>(theme.filters[0] ?? "Todos");

  useEffect(() => {
    setActiveFilter(theme.filters[0] ?? "Todos");
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [theme.slug, theme.filters]);

  const breadcrumb = (
    <nav aria-label="Breadcrumb" className="text-[13px] text-foreground-600 mb-5">
      <ol className="flex items-center gap-1.5">
        <li>
          <Link to="/" className="hover:underline cursor-pointer">
            Início
          </Link>
        </li>
        <li>
          <i className="ri-arrow-right-s-line text-foreground-400"></i>
        </li>
        <li className="text-foreground-900">{theme.title}</li>
      </ol>
    </nav>
  );

  const GiftsHero = (
    <section className="relative w-full px-4 md:px-8 pt-6 md:pt-8 pb-6 overflow-hidden">
      <div className="pointer-events-none absolute -top-10 -right-10 w-48 h-48 rounded-full bg-primary-100/50 blur-2xl"></div>
      <div className="pointer-events-none absolute top-6 right-[18%] w-20 h-20 rounded-full border border-primary-200/70"></div>

      <div className="relative max-w-[1280px] mx-auto">
        {breadcrumb}

        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div className="flex items-end gap-4">
            <span className="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full bg-primary-500 text-background-50 text-[11px] font-medium tracking-wide uppercase mb-2 whitespace-nowrap">
              <i className="ri-gift-line text-[12px]"></i>
              Guia de presentes
            </span>
            <h1 className="font-heading text-foreground-950 text-4xl md:text-5xl leading-none tracking-[-0.01em] flex items-baseline gap-2">
              Presentes
              <span className="font-sans text-[13px] font-normal text-foreground-500 tracking-normal">
                · {products.length || 0}
              </span>
            </h1>
          </div>
          <Link
            to="/gift-cards"
            className="group inline-flex items-center gap-1.5 text-[13px] text-foreground-700 hover:text-primary-600 cursor-pointer whitespace-nowrap transition-colors"
          >
            <span className="underline underline-offset-4 decoration-foreground-300 group-hover:decoration-primary-500">
              Vale-presente
            </span>
            <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5"></i>
          </Link>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <span className="w-1.5 h-1.5 rounded-full bg-primary-500"></span>
          <span className="flex-1 h-px bg-background-200"></span>
        </div>
      </div>
    </section>
  );

  const HomeHero = (
    <section className="w-full px-4 md:px-8 pt-8 md:pt-12 pb-10">
      <div className="max-w-[1280px] mx-auto">
        {breadcrumb}

        <div className="relative w-full h-[480px] md:h-[560px] rounded-[24px] overflow-hidden bg-background-200">
          <img
            src={HERO_BY_SLUG["home-favorites"]}
            alt="Favoritos da Casa"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/55 via-foreground-950/10 to-transparent"></div>

          <div className="absolute left-6 md:left-10 bottom-6 md:bottom-10">
            <h1 className="font-heading text-background-50 text-4xl md:text-6xl lg:text-7xl leading-none tracking-[-0.01em] mb-5">
              Favoritos da Casa
            </h1>
            <a
              href="#grid"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-background-50 hover:bg-background-100 text-foreground-950 text-[14px] font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              Comprar
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </div>
    </section>
  );

  const DefaultHero = (
    <section className="w-full px-4 md:px-8 pt-10 pb-10">
      <div className="max-w-[1280px] mx-auto">
        {breadcrumb}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-7 lg:gap-10 items-stretch">
          <div className="lg:col-span-5 flex flex-col justify-center">
            <h1 className="font-heading text-foreground-950 text-5xl md:text-6xl leading-none tracking-[-0.01em] mb-7">
              {theme.title}
            </h1>
            <div className="flex items-center gap-3 flex-wrap">
              <a
                href="#grid"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-foreground-950 hover:bg-foreground-800 text-background-50 text-[14px] font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                Comprar
                <i className="ri-arrow-right-line"></i>
              </a>
              <Link
                to="/"
                className="inline-flex items-center gap-2 h-12 px-5 rounded-full border border-background-300 hover:border-foreground-900 text-foreground-900 text-[14px] font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                Todos
              </Link>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="relative w-full aspect-[16/10] rounded-3xl overflow-hidden bg-background-100">
              <img
                src={HERO_BY_SLUG[theme.slug] ?? HERO_BY_SLUG.gifts}
                alt={theme.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  const heroBlock =
    theme.slug === "gifts" ? GiftsHero : theme.slug === "home-favorites" ? HomeHero : DefaultHero;

  return (
    <div className="min-h-screen bg-background-50 text-foreground-900 flex flex-col">
      <Navbar />

      <main className="flex-1">
        {heroBlock}

        {/* Filter strip */}
        <section className="w-full px-4 md:px-8 border-y border-background-200/60 bg-background-100/40">
          <div className="max-w-[1280px] mx-auto py-4 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              {theme.filters.map((f) => {
                const active = activeFilter === f;
                return (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setActiveFilter(f)}
                    className={`inline-flex items-center h-9 px-4 rounded-full border text-[13px] font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      active
                        ? "bg-foreground-950 text-background-50 border-foreground-950"
                        : "bg-background-50 text-foreground-900 border-background-300 hover:border-foreground-900"
                    }`}
                  >
                    {f}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-2 text-[13px] text-foreground-700">
              <i className="ri-arrow-up-down-line"></i>
              <span className="text-foreground-950 font-medium">Mais populares</span>
            </div>
          </div>
        </section>

        {/* Grid */}
        <section id="grid" className="w-full px-4 md:px-8 py-10 md:py-14 scroll-mt-24">
          <div className="max-w-[1280px] mx-auto">
            {loading && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="aspect-square rounded-md bg-background-200/60"></div>
                    <div className="h-3 bg-background-200/60 rounded mt-3 w-3/4"></div>
                    <div className="h-3 bg-background-200/60 rounded mt-2 w-1/3"></div>
                  </div>
                ))}
              </div>
            )}

            {!loading && error && (
              <div className="py-16 text-center">
                <p className="text-[14px] text-foreground-700 mb-4">
                  Não foi possível carregar: {error}
                </p>
                <button
                  type="button"
                  onClick={refetch}
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-[14px] font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line"></i>
                  Tentar novamente
                </button>
              </div>
            )}

            {!loading && !error && products.length === 0 && (
              <div className="py-20 text-center">
                <p className="font-heading text-foreground-950 text-2xl mb-4">
                  Nada por aqui ainda.
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-foreground-950 hover:bg-foreground-800 text-background-50 text-[14px] font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  Ver todos
                </Link>
              </div>
            )}

            {!loading && !error && products.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6">
                {products.map((p) => (
                  <ProductCard
                    key={p.id}
                    product={p}
                    variants={variantsByProduct[p.id] ?? []}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}